import { useState } from 'react';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import { Input } from './Input';
import { CashPlanPicker, type CashPlanSelection } from './CashPlanPicker';
import { formatDate } from '../lib/format';
import type { CashPlanChoice } from '../lib/plan-catalog';
import { recordCashPayment, CASH_METHOD_LABEL, type CashMethod } from '../api/billing';

/**
 * Record an off-gateway payment (cash / bKash / Nagad / bank transfer) against a
 * business. Shared by the reseller CustomerDetail + admin BusinessDetail "Record
 * payment" actions. Calls POST /payments/cash (extends the subscription, writes a
 * paid invoice, and the correct reseller_ledger entry for reseller-collected money).
 * The parent supplies `onRecorded` to invalidate its own queries.
 *
 * A business with NO live subscription (expired / canceled / none) must NAME the plan the
 * payment starts — `needsPlan` + `plans` (2026-09-15). Without it the API used to invent a
 * package-less monthly plan at the typed amount; now it refuses (409 PLAN_REQUIRED), and if a
 * parent's view was stale and it still lands here, the picker is revealed from that reply.
 */
export function CashPaymentModal({
  open,
  onClose,
  businessId,
  businessName,
  onRecorded,
  needsPlan = false,
  plans = [],
  catalogLabel,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  onRecorded?: () => void;
  /** True when the business has no live subscription — the payment starts a plan. */
  needsPlan?: boolean;
  /** The plans it may be started on (the business's own catalog). */
  plans?: readonly CashPlanChoice[];
  catalogLabel?: string;
}) {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<CashMethod>('manual_cash');
  const [plan, setPlan] = useState<CashPlanSelection | null>(null);
  const [planRequired, setPlanRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ invoiceNo: string; currentPeriodEnd: string | null; status: string; extended: boolean; creditMinor: number; startedPlan: string | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const amountNum = Number(amount) || 0;
  const showPicker = needsPlan || planRequired;
  const valid = amountNum > 0 && (!showPicker || !!plan);

  function close() {
    onClose();
    setTimeout(() => {
      setAmount('');
      setMethod('manual_cash');
      setPlan(null);
      setPlanRequired(false);
      setResult(null);
      setError(null);
    }, 250);
  }

  function choosePlan(next: CashPlanSelection | null) {
    // Prefill the amount with the plan price when nothing (or the previous plan's price) is typed.
    const prevPrice = plan ? String(plan.priceMinor / 100) : '';
    if (next && (amount === '' || amount === prevPrice)) setAmount(String(next.priceMinor / 100));
    setPlan(next);
  }

  async function save() {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await recordCashPayment({
        businessId, amountMinor: Math.round(amountNum * 100), method,
        ...(showPicker && plan ? { packageId: plan.packageId, interval: plan.interval } : {}),
      });
      onRecorded?.();
      setResult({
        invoiceNo: res.invoiceNo, currentPeriodEnd: res.currentPeriodEnd, status: res.status,
        extended: res.extended !== false, creditMinor: res.creditMinor ?? 0, startedPlan: res.startedPlan ?? null,
      });
    } catch (e) {
      const err = e as { status?: number; message?: string; code?: string };
      // The API says exactly what is wrong (plan needed, short of the price, wrong catalog,
      // unpaid invoice…) — show that, not a generic line that hides it.
      if (err?.code === 'PLAN_REQUIRED' || /no live subscription/i.test(err?.message ?? '')) setPlanRequired(true);
      setError(
        err?.status === 403
          ? 'You are not allowed to record this payment.'
          : err?.message || 'Could not record the payment. Check the amount and try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={result ? 'Payment recorded' : `Record payment · ${businessName}`}
      size="md"
      footer={
        result ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button onClick={save} loading={submitting} disabled={!valid}>Record payment</Button>
          </>
        )
      }
    >
      {result && !result.extended ? (
        // Recorded, but it did not cover what was owed — say so plainly instead of "active".
        <div className="text-center py-6">
          <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 dark:bg-amber-950/40 grid place-items-center text-amber-700 dark:text-amber-300 mb-4">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">Payment recorded — not renewed</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {result.creditMinor > 0 ? (
              <>
                ৳{(result.creditMinor / 100).toLocaleString('en-US')} is kept as <strong>credit</strong>. It is less than one
                billing period, so the subscription was not renewed and stays <strong>{result.status.replace(/_/g, ' ')}</strong>.
                The credit comes off the next invoice.
              </>
            ) : (
              <>
                Applied as a part-payment to the open invoice. The subscription stays{' '}
                <strong>{result.status.replace(/_/g, ' ')}</strong> until the rest is paid.
              </>
            )}
          </p>
        </div>
      ) : result ? (
        <div className="text-center py-6">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-950/40 grid place-items-center text-emerald-700 dark:text-emerald-300 mb-4">
            <CheckCircle className="w-6 h-6" />
          </div>
          <h3 className="text-base font-semibold text-zinc-950 dark:text-zinc-50">
            {result.startedPlan ? 'Subscription started' : 'Payment recorded'}
          </h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {result.invoiceNo ? <>Invoice <span className="mono">{result.invoiceNo}</span> · </> : null}
            {result.startedPlan ? <>now on <strong>{result.startedPlan}</strong>, active</> : <>subscription active</>}
            {result.currentPeriodEnd ? <> until <span className="tabular-nums">{formatDate(result.currentPeriodEnd)}</span></> : ''}.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {error && (
            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 rounded-md px-3 py-2">{error}</div>
          )}
          {showPicker && (
            <CashPlanPicker
              plans={plans}
              value={plan ? { packageId: plan.packageId, interval: plan.interval } : null}
              onChange={choosePlan}
              catalogLabel={catalogLabel}
            />
          )}
          <Input
            label="Amount (BDT)"
            required
            type="number"
            min={0}
            prefix={<span className="text-sm">৳</span>}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            hint={showPicker && plan ? `Starting a plan needs its full price — ৳${(plan.priceMinor / 100).toLocaleString('en-US')} for this one.` : undefined}
          />
          <div>
            <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Method</label>
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as CashMethod)}
              className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 text-zinc-950 dark:text-zinc-50 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand"
            >
              {(Object.keys(CASH_METHOD_LABEL) as CashMethod[]).map((m) => (
                <option key={m} value={m}>{CASH_METHOD_LABEL[m]}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {showPicker ? (
              <>
                Records an off-gateway payment and starts the chosen plan for one billing period from today. The amount must
                cover the plan price. For reseller-collected money the platform commission is settled automatically on the
                reseller ledger.
              </>
            ) : (
              <>
                Records an off-gateway payment. It renews or restores the subscription only when it covers what is owed — the
                open invoice, or one full billing period if nothing is open. A smaller amount is a part-payment, or is kept as
                credit. For reseller-collected money the platform commission is settled automatically on the reseller ledger.
              </>
            )}
          </p>
        </div>
      )}
    </Modal>
  );
}
